import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Project } from '@/types';
import { calculateProjectBudgets } from '@/lib/utils/dashboard';
import { AlertCircle, CheckCircle2, AlertTriangle } from 'lucide-react';

interface BudgetDashboardProps {
  projects: Project[];
}

export function BudgetDashboard({ projects }: BudgetDashboardProps) {
  const projectBudgets = calculateProjectBudgets(projects);

  const totalBudget = projects.reduce((sum, p) => sum + p.budgetDays, 0);
  const totalConsumed = projects.reduce((sum, p) => sum + p.consumedDays, 0);
  const totalRemaining = totalBudget - totalConsumed;
  const overallPercentage = (totalConsumed / totalBudget) * 100;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'green':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'amber':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'red':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'green':
        return 'bg-green-500';
      case 'amber':
        return 'bg-amber-500';
      case 'red':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'green':
        return 'On Track';
      case 'amber':
        return 'At Risk';
      case 'red':
        return 'Over Budget';
      default:
        return 'Unknown';
    }
  };

  const greenCount = projectBudgets.filter((p) => p.status === 'green').length;
  const amberCount = projectBudgets.filter((p) => p.status === 'amber').length;
  const redCount = projectBudgets.filter((p) => p.status === 'red').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resource Budget Dashboard</CardTitle>
        <CardDescription>
          Monthly budget allocation and consumption tracking
        </CardDescription>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm text-muted-foreground">Total Budget</p>
            <p className="text-2xl font-bold">{totalBudget} days</p>
          </div>
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm text-muted-foreground">Consumed</p>
            <p className="text-2xl font-bold">{totalConsumed} days</p>
          </div>
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm text-muted-foreground">Remaining</p>
            <p className="text-2xl font-bold">{totalRemaining} days</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span>Overall Budget Utilization</span>
            <span className="font-medium">{Math.round(overallPercentage)}%</span>
          </div>
          <Progress value={overallPercentage} className="h-2" />
        </div>
        <div className="flex gap-4 mt-4">
          <Badge variant="outline" className="gap-1">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            On Track: {greenCount}
          </Badge>
          <Badge variant="outline" className="gap-1">
            <div className="h-2 w-2 rounded-full bg-amber-500" />
            At Risk: {amberCount}
          </Badge>
          <Badge variant="outline" className="gap-1">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            Over Budget: {redCount}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <h4 className="font-semibold text-sm">Project Budget Status</h4>
          {projectBudgets.map((project) => {
            const percentage = (project.consumedDays / project.budgetDays) * 100;
            
            return (
              <div
                key={project.projectId}
                className="p-4 rounded-lg border bg-card space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(project.status)}
                    <h5 className="font-medium">{project.projectName}</h5>
                  </div>
                  <Badge
                    variant="outline"
                    className={`${getStatusColor(project.status)} text-white border-none`}
                  >
                    {getStatusLabel(project.status)}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Budget</p>
                    <p className="font-medium">{project.budgetDays} days</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Consumed</p>
                    <p className="font-medium">{project.consumedDays} days</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Remaining</p>
                    <p className={`font-medium ${project.remainingDays < 0 ? 'text-red-500' : ''}`}>
                      {project.remainingDays} days
                    </p>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Utilization</span>
                    <span className="font-medium">{Math.round(percentage)}%</span>
                  </div>
                  <Progress 
                    value={Math.min(percentage, 100)} 
                    className={`h-2 ${percentage > 100 ? '[&>div]:bg-red-500' : ''}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
